import React, { useEffect, useState, useRef } from "react";
import { X, Sparkles, Mail, Loader2, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

const STORAGE_KEY = "xookpub_exit_intent_v1";

/**
 * Exit-intent modal.
 * Fires when the user moves the cursor above the top edge of the viewport
 * (intent to close the tab / switch). Only shown once per browser (localStorage).
 * On mobile (no mouseleave fired from top), shown after 60s of idle scroll.
 */
export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const triggered = useRef(false);

  useEffect(() => {
    // Skip on auth/legal/studio pages and brief tracking flows.
    const path = window.location.pathname;
    const blocked = ["/login", "/dashboard", "/studio", "/legal", "/payment-success", "/brief", "/verify-email", "/reset-password", "/forgot-password"];
    if (blocked.some(p => path.startsWith(p))) return;

    if (sessionStorage.getItem(STORAGE_KEY) === "shown") return;
    if (localStorage.getItem(STORAGE_KEY) === "dismissed") return;

    const trigger = () => {
      if (triggered.current) return;
      triggered.current = true;
      sessionStorage.setItem(STORAGE_KEY, "shown");
      setOpen(true);
    };

    // Desktop: detect mouse leaving the top of the viewport
    const onMouseLeave = (e) => {
      if (e.clientY <= 0) trigger();
    };
    document.addEventListener("mouseleave", onMouseLeave);

    // Mobile fallback: trigger after 60s if user is still on the page
    const t = setTimeout(trigger, 60000);

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      clearTimeout(t);
    };
  }, []);

  const close = (permanent = false) => {
    if (permanent) localStorage.setItem(STORAGE_KEY, "dismissed");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError("Email invalide");
      return;
    }
    setLoading(true); setError("");
    try {
      await api.post("/newsletter/subscribe", { email, source: "exit_intent" });
      setSuccess(true);
      localStorage.setItem(STORAGE_KEY, "dismissed");
      setTimeout(() => setOpen(false), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="exit-intent-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={() => close(false)}
      />

      {/* Card */}
      <div className="relative gold-border-gradient max-w-md w-full p-8 text-center animate-in fade-in zoom-in-95 duration-300">
        <button
          aria-label="Fermer"
          data-testid="exit-intent-close"
          onClick={() => close(true)}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60"
        >
          <X className="w-4 h-4" />
        </button>

        {success ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-gold mx-auto" />
            <h2 className="font-heading text-2xl mt-4">Merci !</h2>
            <p className="text-white/70 text-sm mt-3">
              {"Vous recevrez nos meilleurs templates IA dans votre boîte mail."}
            </p>
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold/30 mb-5">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              <span className="label-mono text-[10px]">{"OFFRE DE BIENVENUE"}</span>
            </div>

            <h2 className="font-heading text-3xl tracking-tight">
              {"Partez avec un "}<span className="gold-text font-semibold">{"flyer offert"}</span>
            </h2>
            <p className="text-white/70 text-sm mt-3 leading-relaxed">
              {"Laissez-nous votre email — on vous envoie un crédit flyer gratuit et nos templates IA exclusifs."}
            </p>

            <form onSubmit={submit} className="mt-6 flex flex-col gap-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="email"
                  data-testid="exit-intent-email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  className="w-full pl-10 pr-3 py-3 rounded-lg bg-obsidian-light border border-white/10 focus:border-gold/50 outline-none text-sm"
                />
              </div>
              {error && (
                <p className="text-red-400 text-xs text-left" data-testid="exit-intent-error">{error}</p>
              )}
              <button
                type="submit"
                data-testid="exit-intent-submit"
                disabled={loading}
                className="gold-btn w-full !py-3 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Je veux mon flyer offert"}
              </button>
              <button
                type="button"
                data-testid="exit-intent-decline"
                onClick={() => close(true)}
                className="text-xs text-white/40 hover:text-white/70 mt-1"
              >
                {"Non merci, je continue à naviguer"}
              </button>
            </form>
            <p className="text-[10px] text-white/30 mt-4">
              {"Pas de spam. Désabonnement en un clic. Conforme RGPD."}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
